// WordPress JS-Enabled HTML Export - Respects ALL Visual Editor configuration

// Default format if none provided
const DEFAULT_FORMAT = {
  start: { background: '#10b981', text: '#ffffff', border: '#10b981' },
  standard: { background: '#667eea', text: '#ffffff', border: '#5a67d8' },
  light: { background: '#a5b4fc', text: '#1e293b', border: '#818cf8' },
  unbounded: { background: 'transparent', text: '#667eea', border: '#667eea' },
  lines: { color: '#94a3b8', activeColor: '#667eea' }
};

// Helper to extract preview text from node body
const getDescriptionPreview = (text, limit = 80) => {
  if (!text) return '';
  // Replace link syntax [[Label|Target]] with just Label, and [[Target]] with Target
  const cleaned = text
    .replace(/\[\[([^|\]]+)\|[^\]]+\]\]/g, '$1')  // [[Label|Target]] -> Label
    .replace(/\[\[([^\]]+)\]\]/g, '$1')            // [[Target]] -> Target
    .trim();
  const lines = cleaned.split('\n').filter(line => line.trim());
  const firstLine = lines[0] || '';
  return firstLine.length > limit ? firstLine.substring(0, limit) + '...' : firstLine;
};

export const generateWordPressHtml = (graph, settings = {}) => {
  const nodes = graph.nodes || [];
  const edges = graph.edges || [];
  
  // Extract ALL configuration from settings
  const {
    nodeFormat = DEFAULT_FORMAT,
    layoutDirection = 'right',
    nodeAlignment = 'center',  // 'center', 'below', 'above'
    showDescription = false,
    descriptionCharLimit = 80,
    showBackLinks = false,
    focusNodeViewEnabled = false,
    focusLayerDepth = 1,
    dynamicViewEnabled = false,
    dynamicViewSize = 'standard'
  } = settings;
  
  // Merge user's nodeFormat with defaults
  const format = {
    start: { ...DEFAULT_FORMAT.start, ...nodeFormat?.start },
    standard: { ...DEFAULT_FORMAT.standard, ...nodeFormat?.standard },
    light: { ...DEFAULT_FORMAT.light, ...nodeFormat?.light },
    unbounded: { ...DEFAULT_FORMAT.unbounded, ...nodeFormat?.unbounded },
    lines: { ...DEFAULT_FORMAT.lines, ...nodeFormat?.lines }
  };
  
  // Node sizing based on dynamicViewSize
  const isCompact = dynamicViewSize === 'compact';
  const nodeW = isCompact ? 160 : 220;
  // CRITICAL: Use taller nodes when showDescription is enabled
  const baseNodeH = isCompact ? 45 : 55;
  const nodeH = showDescription ? baseNodeH + 25 : baseNodeH;  // Add height for description
  const gapX = isCompact ? 60 : 80;
  const gapY = isCompact ? 15 : 20;
  
  const positions = new Map();
  const paths = [];
  
  // Build parent-child map - only forward edges (no back links)
  const childrenMap = new Map();
  const hasParent = new Set();
  
  // First pass: collect all edges
  const allEdges = [];
  edges.forEach(edge => {
    const fromId = edge.from || edge.source;
    const toId = edge.to || edge.target;
    if (fromId && toId && fromId !== toId) {
      allEdges.push({ from: fromId, to: toId });
    }
  });
  
  // Find root node first
  const tempHasParent = new Set();
  allEdges.forEach(e => tempHasParent.add(e.to));
  
  const rootNode = nodes.find(n => (n.name === 'Home' || n.name === 'Start') && !tempHasParent.has(n.id)) 
    || nodes.find(n => !tempHasParent.has(n.id)) 
    || nodes[0];
  
  if (!rootNode) return `<div>No nodes to display</div>`;
  
  // Build tree from root - only include edges that go AWAY from root (no back links)
  const visited = new Set();
  const buildTree = (nodeId) => {
    if (visited.has(nodeId)) return;
    visited.add(nodeId);
    
    // Find edges FROM this node that go to unvisited nodes (forward edges only)
    const forwardEdges = allEdges.filter(e => e.from === nodeId && !visited.has(e.to));
    
    if (forwardEdges.length > 0) {
      childrenMap.set(nodeId, forwardEdges.map(e => e.to));
      forwardEdges.forEach(e => {
        hasParent.add(e.to);
        buildTree(e.to);
      });
    }
  };
  buildTree(rootNode.id);
  
  const nodeMap = new Map(nodes.map(n => [n.id, n]));
  
  const getNodeColors = (node, isRoot) => {
    if (isRoot) return format.start;
    const style = node.nodeStyle || 'standard';
    return format[style] || format.standard;
  };
  
  // Phase 1: Calculate subtree sizes (number of leaf descendants)
  const subtreeSizes = new Map();
  const calcSize = (nodeId, visitedCalc = new Set()) => {
    if (visitedCalc.has(nodeId)) return 0;
    visitedCalc.add(nodeId);
    const children = childrenMap.get(nodeId) || [];
    if (children.length === 0) {
      subtreeSizes.set(nodeId, 1);
      return 1;
    }
    const size = children.reduce((sum, cid) => sum + calcSize(cid, visitedCalc), 0);
    subtreeSizes.set(nodeId, size);
    return size;
  };
  calcSize(rootNode.id);
  
  // Phase 2: Position nodes using subtree sizes and respecting nodeAlignment
  const positionNode = (nodeId, depth, yStart, visitedPos = new Set()) => {
    if (visitedPos.has(nodeId)) return yStart;
    visitedPos.add(nodeId);
    
    const node = nodeMap.get(nodeId);
    if (!node) return yStart;
    
    const children = childrenMap.get(nodeId) || [];
    const x = 30 + depth * (nodeW + gapX);
    
    // Calculate total height needed for this subtree using actual nodeH
    const subtreeSize = subtreeSizes.get(nodeId) || 1;
    const subtreeHeight = subtreeSize * (nodeH + gapY) - gapY;
    
    // Position based on nodeAlignment setting
    let y;
    if (children.length === 0) {
      // Leaf node - always at yStart
      y = yStart;
    } else if (nodeAlignment === 'below') {
      // Parent aligns with TOP of children (first child)
      y = yStart;
    } else if (nodeAlignment === 'above') {
      // Parent aligns with BOTTOM of children (last child position)
      y = yStart + subtreeHeight - nodeH;
    } else {
      // 'center' (default) - Parent centered among children
      y = yStart + (subtreeHeight - nodeH) / 2;
    }
    
    const isRoot = depth === 0;
    const colors = getNodeColors(node, isRoot);
    
    // Get description if showDescription is enabled - use helper that preserves link labels
    const desc = showDescription ? getDescriptionPreview(node.body, descriptionCharLimit) : '';
    
    positions.set(nodeId, { 
      id: node.id, 
      name: node.displayName || node.name, 
      x, 
      y, 
      bg: colors.background,
      text: colors.text,
      border: colors.border,
      url: node.url,
      desc
    });
    
    // Position children
    let childY = yStart;
    for (const childId of children) {
      const childSize = subtreeSizes.get(childId) || 1;
      const childHeight = childSize * (nodeH + gapY) - gapY;
      positionNode(childId, depth + 1, childY, visitedPos);
      childY += childHeight + gapY;
    }
    
    return yStart + subtreeHeight;
  };
  
  positionNode(rootNode.id, 0, 30);
  
  // Phase 3: Draw connections using correct node height for centering
  for (const [parentId, children] of childrenMap) {
    const parentPos = positions.get(parentId);
    if (!parentPos) continue;
    
    for (const childId of children) {
      const childPos = positions.get(childId);
      if (!childPos) continue;
      
      const px = parentPos.x + nodeW;
      const py = parentPos.y + nodeH / 2;  // Use actual nodeH
      const cx = childPos.x;
      const cy = childPos.y + nodeH / 2;   // Use actual nodeH
      const mx = px + (cx - px) * 0.5;
      paths.push(`<path d="M${px} ${py} C${mx} ${py}, ${mx} ${cy}, ${cx} ${cy}" fill="none" stroke="${format.lines.color}" stroke-width="2.5"/>`);
    }
  }
  
  const posArray = Array.from(positions.values());
  const w = Math.max(...posArray.map(n => n.x), 100) + nodeW + 50;
  const h = Math.max(...posArray.map(n => n.y), 100) + nodeH + 50;
  const title = graph.storyTitle || 'PathNav';
  
  const nodesSvg = posArray.map(n => {
    const isTransparent = n.bg === 'transparent';
    const textColor = n.text;
    const linkHtml = n.url ? `<a href="${n.url}" target="_blank" class="pn-link" style="color:${textColor};"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg></a>` : '';
    
    // When showDescription is enabled, use two-part node structure (colored header + white content)
    if (showDescription && n.desc) {
      const headerBg = isTransparent ? `background:transparent;border:2px dashed ${n.border};` : `background:${n.bg};`;
      return `<div class="pn-node pn-node-with-desc" data-id="${n.id}" style="left:${n.x}px;top:${n.y}px;width:${nodeW}px;height:${nodeH}px;">
        <div class="pn-node-header" style="${headerBg}color:${textColor};">
          <span class="pn-name">${n.name}</span>${linkHtml}
        </div>
        <div class="pn-node-content">
          <div class="pn-desc">${n.desc}</div>
        </div>
      </div>`;
    }
    
    // Simple node without description
    const bgStyle = isTransparent ? `background:transparent;border:2px dashed ${n.border};` : `background:${n.bg};`;
    return `<div class="pn-node" data-id="${n.id}" style="left:${n.x}px;top:${n.y}px;width:${nodeW}px;height:${nodeH}px;${bgStyle}color:${textColor};"><div class="pn-header"><span class="pn-name">${n.name}</span>${linkHtml}</div></div>`;
  }).join('');

  return `<!DOCTYPE html>
<html><head><meta charset="UTF-8"><meta name="viewport" content="width=device-width,initial-scale=1"><title>${title}</title>
<style>
.pn-wrap{font-family:system-ui,-apple-system,sans-serif;padding:20px;}
.pn-title{font-size:18px;font-weight:600;margin-bottom:16px;color:#1e293b;}
.pn-canvas{position:relative;width:${w}px;height:${h}px;background:#f8fafc;border-radius:16px;overflow:auto;}
.pn-svg{position:absolute;top:0;left:0;pointer-events:none;}
.pn-node{position:absolute;border-radius:12px;padding:0 20px;font-size:15px;font-weight:500;cursor:pointer;display:flex;align-items:center;justify-content:space-between;transition:all .15s;box-shadow:0 2px 8px rgba(0,0,0,.12);}
.pn-node:hover{transform:scale(1.02);box-shadow:0 6px 16px rgba(0,0,0,.18);}
.pn-node.selected{outline:3px solid #f97316;outline-offset:3px;}
.pn-node-with-desc{padding:0;display:flex;flex-direction:column;overflow:hidden;background:#fff;border:1px solid #e2e8f0;}
.pn-node-with-desc .pn-node-header{display:flex;align-items:center;justify-content:space-between;padding:8px 16px;width:100%;box-sizing:border-box;border-radius:11px 11px 0 0;}
.pn-node-with-desc .pn-node-content{flex:1;padding:8px 16px;background:#fff;width:100%;box-sizing:border-box;display:flex;align-items:flex-start;}
.pn-header{display:flex;align-items:center;justify-content:space-between;width:100%;}
.pn-name{flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
.pn-desc{font-size:12px;color:#475569;line-height:1.4;overflow:hidden;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;}
.pn-link{display:flex;align-items:center;justify-content:center;width:32px;height:32px;background:rgba(255,255,255,0.25);border-radius:50%;margin-left:10px;text-decoration:none;transition:background .15s;flex-shrink:0;}
.pn-link:hover{background:rgba(255,255,255,0.4);}
</style></head>
<body><div class="pn-wrap">
<div class="pn-title">${title}</div>
<div class="pn-canvas">
<svg class="pn-svg" width="${w}" height="${h}">${paths.join('')}</svg>
${nodesSvg}
</div></div>
<script>
document.querySelectorAll('.pn-node').forEach(n=>n.onclick=e=>{
if(e.target.closest('.pn-link'))return;
document.querySelectorAll('.pn-node.selected').forEach(s=>s.classList.remove('selected'));
n.classList.add('selected');});
</script></body></html>`;
};

// Fallback copy function for when Clipboard API is blocked
const fallbackCopyToClipboard = (text) => {
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.style.position = 'fixed';
  textarea.style.left = '-9999px';
  textarea.style.top = '-9999px';
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();
  try {
    document.execCommand('copy');
    return true;
  } catch (err) {
    console.error('Fallback copy failed:', err);
    return false;
  } finally {
    document.body.removeChild(textarea);
  }
};

export const copyWordPressHtml = async (graph, nodeFormats = null) => {
  const html = generateWordPressHtml(graph, nodeFormats);
  try {
    await navigator.clipboard.writeText(html);
  } catch (err) {
    // Fallback for when Clipboard API is blocked
    if (!fallbackCopyToClipboard(html)) {
      throw new Error('Unable to copy to clipboard. Please try again.');
    }
  }
  return html;
};
